import { useState, useEffect, useCallback, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { updateEvent } from "@/services/api/events";
import { getEventLandingSettings, updateEventLandingSettings, type EventCeremony, type EventLandingSettings } from "@/services/api/eventSettings";
import { useTranslation } from "@/hooks/useTranslation";
import { combineAutosaveStatus, readAutosaveDraft, useAutosave } from "@/hooks/useAutosave";
import { usePendingSaves, waitForPendingSaves } from "@/hooks/autosaveRegistry";
import type { Event } from "@/services/api/events";

const eventSchema = z.object({
  event_name: z.string().min(1, "Event name is required"),
  // JUB-154: the date may be undecided ("Nu am stabilit data").
  event_date: z.string(),
  event_star_bride: z.string().optional(),
  event_star_groom: z.string().optional(),
  event_star_baptism: z.string().optional(),
  event_star_baptism_2: z.string().optional(),
  event_includes_wedding: z.boolean().optional(),
  event_includes_baptism: z.boolean().optional(),
  event_baby_count: z.union([z.literal(1), z.literal(2)]).nullable().optional(),
  event_description: z.string().optional(),
});

export type EventFormData = z.infer<typeof eventSchema>;

interface CeremonyData {
  date: string;
  time: string;
  locationName: string;
  address: string;
}

/** JUB-154: "baptism" is the baptism held separately from the religious wedding. */
type CeremonyType = "civil" | "religious" | "party" | "baptism";
type Ceremonies = Record<CeremonyType, CeremonyData & { enabled: boolean }>;

interface FamilyData {
  brideParent1: string;
  brideParent2: string;
  groomParent1: string;
  groomParent2: string;
  godparent1: string;
  godparent2: string;
  brideParentsEnabled: boolean;
  groomParentsEnabled: boolean;
  godparentsEnabled: boolean;
}

interface UseGeneralFormProps {
  event: Event;
  onEventUpdated?: () => void;
  /** JUB-152: the event as the server acknowledged it, without a reload. */
  onEventPatched?: (event: Event) => void;
}

/** PUT /events/{uuid} payload — the server shape the autosave diffs (JUB-152). */
export type GeneralEventPayload = {
  name: string;
  /** JUB-154: null while the date is undecided. */
  date: string | null;
  starBride: string | null;
  starGroom: string | null;
  starBaptism: string | null;
  starBaptism2: string | null;
  includesWedding: boolean;
  includesBaptism: boolean;
  babyCount: 1 | 2 | null;
  description: string | null;
};

/** PUT /events/{uuid}/landing-settings payload for this tab (JUB-152). */
export type GeneralLandingPayload = {
  ceremonies: Record<CeremonyType, { enabled: boolean; venue: string | null; address: string | null; datetime: string | null }>;
  family: { parents: string; godparents: string };
};

const EVENT_KEYS = [
  "name", "date", "starBride", "starGroom", "starBaptism", "starBaptism2",
  "includesWedding", "includesBaptism", "babyCount", "description",
] as const;
/** Name/date/couple/baby names drive the public slug: save them on blur, not per keystroke. */
const EVENT_BLUR_ONLY = ["name", "date", "starBride", "starGroom", "starBaptism", "starBaptism2"] as const;
/** The server validates the event type with the baby fields of the same request. */
const EVENT_TYPE_GROUP = ["includesWedding", "includesBaptism", "babyCount", "starBaptism", "starBaptism2"] as const;
const LANDING_KEYS = ["ceremonies", "family"] as const;
const INCOMPLETE_DATETIME = "__incomplete__";
const EVENT_FORM_ID = "general-event";
const LANDING_FORM_ID = "general-landing";

/** Unsaved edits from an earlier visit, laid over the server values (JUB-152). */
function applyEventDraft(defaults: EventFormData, draft: Partial<GeneralEventPayload> | undefined): EventFormData {
  if (!draft) return defaults;
  const out = { ...defaults };
  if ("name" in draft) out.event_name = draft.name ?? "";
  if ("date" in draft) out.event_date = draft.date ?? "";
  if ("starBride" in draft) out.event_star_bride = draft.starBride ?? "";
  if ("starGroom" in draft) out.event_star_groom = draft.starGroom ?? "";
  if ("starBaptism" in draft) out.event_star_baptism = draft.starBaptism ?? "";
  if ("starBaptism2" in draft) out.event_star_baptism_2 = draft.starBaptism2 ?? "";
  if ("includesWedding" in draft) out.event_includes_wedding = !!draft.includesWedding;
  if ("includesBaptism" in draft) out.event_includes_baptism = !!draft.includesBaptism;
  if ("babyCount" in draft) out.event_baby_count = draft.babyCount ?? null;
  if ("description" in draft) out.event_description = draft.description ?? "";
  return out;
}

function applyLandingDraft(settings: EventLandingSettings, draft: Partial<GeneralLandingPayload> | undefined): EventLandingSettings {
  if (!draft) return settings;
  const ceremonies = { ...settings.ceremonies };
  for (const type of ["civil", "religious", "party", "baptism"] as const) {
    const patch = draft.ceremonies?.[type] as Partial<GeneralLandingPayload["ceremonies"]["civil"]> | undefined;
    if (!patch) continue;
    const next = { ...(ceremonies[type] ?? { name: type, enabled: false, venue: null, address: null, datetime: null }) };
    if ("enabled" in patch) next.enabled = !!patch.enabled;
    if ("venue" in patch) next.venue = patch.venue ?? null;
    if ("address" in patch) next.address = patch.address ?? null;
    // A half-typed date/time cannot be rebuilt from the payload; keep the server value.
    if ("datetime" in patch && patch.datetime !== INCOMPLETE_DATETIME) next.datetime = patch.datetime ?? null;
    ceremonies[type] = next;
  }
  const family = { ...settings.family, ...(draft.family ?? {}) };
  return { ...settings, ceremonies, family };
}

export function formDefaultsFromEvent(event: Event): EventFormData {
  return {
    event_name: event.name || "",
    event_date: event.date ? new Date(event.date).toISOString().split("T")[0] : "",
    event_star_bride: event.starBride || "",
    event_star_groom: event.starGroom || "",
    event_star_baptism: event.starBaptism || "",
    event_star_baptism_2: event.starBaptism2 || "",
    event_includes_wedding: event.includesWedding ?? true,
    event_includes_baptism: event.includesBaptism ?? false,
    event_baby_count: event.babyCount === 1 || event.babyCount === 2 ? event.babyCount : null,
    event_description: event.description || "",
  };
}

export function serializeEventForm(data: EventFormData): GeneralEventPayload {
  return {
    name: data.event_name ?? "",
    date: data.event_date || null,
    starBride: data.event_star_bride || null,
    starGroom: data.event_star_groom || null,
    starBaptism: data.event_star_baptism || null,
    starBaptism2: data.event_star_baptism_2 || null,
    includesWedding: data.event_includes_wedding ?? true,
    includesBaptism: data.event_includes_baptism ?? false,
    babyCount: data.event_baby_count === 1 || data.event_baby_count === 2 ? data.event_baby_count : null,
    // Cleared text is sent as null so the server clears it (JUB-152).
    description: data.event_description || null,
  };
}

// Format parents text for backend
function formatParentsText(data: FamilyData): string {
  const parts: string[] = [];
  if (data.brideParentsEnabled && (data.brideParent1 || data.brideParent2)) {
    const brideNames = [data.brideParent1, data.brideParent2].filter(Boolean).join(" și ");
    if (brideNames) {
      parts.push(`Părinții Miresei: ${brideNames}`);
    }
  }
  if (data.groomParentsEnabled && (data.groomParent1 || data.groomParent2)) {
    const groomNames = [data.groomParent1, data.groomParent2].filter(Boolean).join(" și ");
    if (groomNames) {
      parts.push(`Părinții Mirelui: ${groomNames}`);
    }
  }
  return parts.join("\n");
}

// Format godparents text for backend
function formatGodparentsText(data: FamilyData): string {
  if (!data.godparentsEnabled) return "";
  return [data.godparent1, data.godparent2].filter(Boolean).join(" și ");
}

export function serializeLanding(ceremonies: Ceremonies, family: FamilyData): GeneralLandingPayload {
  const ceremony = (c: CeremonyData & { enabled: boolean }) => ({
    enabled: c.enabled,
    venue: c.locationName || null,
    address: c.address || null,
    // Send the wall-clock time verbatim: toISOString() converted the
    // organizer's local time to UTC, shifting every ceremony -2/-3h on the
    // public page. A half-filled date/time is withheld until complete.
    datetime: c.date && c.time ? `${c.date}T${c.time}:00` : !c.date && !c.time ? null : INCOMPLETE_DATETIME,
  });
  return {
    ceremonies: {
      civil: ceremony(ceremonies.civil),
      religious: ceremony(ceremonies.religious),
      party: ceremony(ceremonies.party),
      baptism: ceremony(ceremonies.baptism),
    },
    family: { parents: formatParentsText(family), godparents: formatGodparentsText(family) },
  };
}

const EMPTY_CEREMONY = { date: "", time: "", locationName: "", address: "", enabled: false };
const EMPTY_FAMILY: FamilyData = {
  brideParent1: "",
  brideParent2: "",
  groomParent1: "",
  groomParent2: "",
  godparent1: "",
  godparent2: "",
  brideParentsEnabled: false,
  groomParentsEnabled: false,
  godparentsEnabled: false,
};

export function useGeneralForm({ event, onEventUpdated, onEventPatched }: UseGeneralFormProps) {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);

  const form = useForm<EventFormData>({
    resolver: zodResolver(eventSchema),
    defaultValues: formDefaultsFromEvent(event),
  });

  const [ceremonies, setCeremonies] = useState<Ceremonies>({
    civil: { ...EMPTY_CEREMONY },
    religious: { ...EMPTY_CEREMONY },
    party: { ...EMPTY_CEREMONY },
    baptism: { ...EMPTY_CEREMONY },
  });
  const [family, setFamily] = useState<FamilyData>(EMPTY_FAMILY);
  /** Serialized snapshot of the loaded ceremonies/family: the landing autosave baseline. */
  const [landingInitial, setLandingInitial] = useState<GeneralLandingPayload | null>(null);

  // JUB-152: an autosave from a previous visit of this tab may still be in
  // flight; wait for it before reading or showing server state.
  const saveInFlight = usePendingSaves(event.uuid);
  const [seededUuid, setSeededUuid] = useState<string | null>(null);
  const [eventInitial, setEventInitial] = useState<GeneralEventPayload | null>(null);

  // Reset the form only for a new event (or once the pending save settled) —
  // never on every refetch, which would overwrite what the user is typing.
  useEffect(() => {
    if (saveInFlight || seededUuid === event.uuid) return;
    const defaults = formDefaultsFromEvent(event);
    const draft = readAutosaveDraft(event.uuid, EVENT_FORM_ID) as Partial<GeneralEventPayload> | undefined;
    form.reset(applyEventDraft(defaults, draft));
    setEventInitial(serializeEventForm(defaults));
    setSeededUuid(event.uuid);
  }, [saveInFlight, seededUuid, event, form]);

  // Load landing settings (ceremonies and family)
  const [landingLoadTick, setLandingLoadTick] = useState(0);
  /** The ceremonies/family baseline could not be loaded; the form stays locked until a retry succeeds. */
  const [landingLoadFailed, setLandingLoadFailed] = useState(false);
  useEffect(() => {
    let isMounted = true;
    setLandingInitial(null);
    setLandingLoadFailed(false);
    async function loadSettings() {
      try {
        setLoading(true);
        await waitForPendingSaves(event.uuid);
        const serverSettings = await getEventLandingSettings(event.uuid);
        if (!isMounted) return;
        const draft = readAutosaveDraft(event.uuid, LANDING_FORM_ID) as Partial<GeneralLandingPayload> | undefined;

        // Parse ceremonies
        const parseCeremony = (ceremony: EventCeremony) => {
          const datetime = ceremony.datetime ? new Date(ceremony.datetime) : null;
          return {
            enabled: ceremony.enabled,
            date: datetime ? datetime.toISOString().split("T")[0] : "",
            time: datetime ? datetime.toTimeString().slice(0, 5) : "",
            locationName: ceremony.venue || "",
            address: ceremony.address || "",
          };
        };

        const parseCeremonies = (settings: EventLandingSettings): Ceremonies => ({
          civil: parseCeremony(settings.ceremonies.civil),
          religious: parseCeremony(settings.ceremonies.religious),
          party: parseCeremony(settings.ceremonies.party),
          baptism: settings.ceremonies.baptism
            ? parseCeremony(settings.ceremonies.baptism)
            : { ...EMPTY_CEREMONY },
        });

        // Parse family data
        // Note: Backend stores parents as formatted text, we'll need to parse it
        // For now, we'll use a simple approach - split by newlines and colons
        const parseParents = (parentsText: string | null | undefined) => {
          if (!parentsText) return { brideParent1: "", brideParent2: "", groomParent1: "", groomParent2: "", enabled: false };

          // Try to parse "Părinții Miresei: Ana și Ion Popescu\nPărinții Mirelui: Elena și Gheorghe Ionescu"
          // Or simpler format with just names
          const lines = parentsText.split("\n").filter(Boolean);
          if (lines.length >= 2) {
            const brideLine = lines[0].includes("Miresei") || lines[0].includes("Bride")
              ? lines[0].split(":").slice(1).join(":").trim()
              : lines[0];
            const groomLine = lines[1].includes("Mirelui") || lines[1].includes("Groom")
              ? lines[1].split(":").slice(1).join(":").trim()
              : lines[1];

            // Try to split by "și" or "and"
            const brideParts = brideLine.split(/și|and/i).map(s => s.trim()).filter(Boolean);
            const groomParts = groomLine.split(/și|and/i).map(s => s.trim()).filter(Boolean);

            return {
              brideParent1: brideParts[0] || "",
              brideParent2: brideParts[1] || "",
              groomParent1: groomParts[0] || "",
              groomParent2: groomParts[1] || "",
              enabled: true,
            };
          }

          // Fallback: try to split by comma or semicolon
          const parts = parentsText.split(/[,;]/).map(s => s.trim()).filter(Boolean);
          return {
            brideParent1: parts[0] || "",
            brideParent2: parts[1] || "",
            groomParent1: parts[2] || "",
            groomParent2: parts[3] || "",
            enabled: parts.length > 0,
          };
        };

        const parseGodparents = (godparentsText: string | null | undefined) => {
          if (!godparentsText) return { godparent1: "", godparent2: "", enabled: false };

          // Try to split by "și" or "and" or comma
          const parts = godparentsText.split(/și|and|[,;]/i).map(s => s.trim()).filter(Boolean);
          return {
            godparent1: parts[0] || "",
            godparent2: parts[1] || "",
            enabled: parts.length > 0,
          };
        };

        const parseFamily = (settings: EventLandingSettings): FamilyData => {
        const parentsData = parseParents(settings.family.parents);
        const godparentsData = parseGodparents(settings.family.godparents);

        return {
          brideParent1: parentsData.brideParent1,
          brideParent2: parentsData.brideParent2,
          groomParent1: parentsData.groomParent1,
          groomParent2: parentsData.groomParent2,
          godparent1: godparentsData.godparent1,
          godparent2: godparentsData.godparent2,
          brideParentsEnabled: parentsData.enabled,
          groomParentsEnabled: parentsData.enabled,
          godparentsEnabled: godparentsData.enabled,
        };
        };

        // The form shows the server values with any unsaved draft laid over
        // them; the baseline is the server values alone, parsed and
        // re-serialized, so loading never saves and the lossy parents
        // parse/format is only sent once the user edits it.
        const shown = applyLandingDraft(serverSettings, draft);
        setCeremonies(parseCeremonies(shown));
        setFamily(parseFamily(shown));
        setLandingInitial(serializeLanding(parseCeremonies(serverSettings), parseFamily(serverSettings)));
      } catch (error) {
        console.error("Failed to load landing settings:", error);
        if (isMounted) setLandingLoadFailed(true);
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    }

    loadSettings();
    return () => {
      isMounted = false;
    };
  }, [event.uuid, landingLoadTick]);

  // ------------------------------------------------------------ autosave
  const values = form.watch();
  const eventCurrent = useMemo(() => serializeEventForm(values), [values]);
  const landingCurrent = useMemo(() => serializeLanding(ceremonies, family), [ceremonies, family]);

  const validateEvent = useCallback((p: GeneralEventPayload): Record<string, string> => {
    const errors: Record<string, string> = {};
    if (!p.name.trim()) errors.name = t("validation.eventNameRequired");
    if (!p.includesWedding && !p.includesBaptism) errors.includesWedding = t("events.form.eventTypeRequired");
    if (p.includesBaptism) {
      if (!p.starBaptism?.trim()) errors.starBaptism = t("events.form.babyNameRequired");
      if (p.babyCount === 2 && !p.starBaptism2?.trim()) errors.starBaptism2 = t("events.form.babyNameRequired");
    }
    return errors;
  }, [t]);

  const validateLanding = useCallback((p: GeneralLandingPayload): Record<string, string> => {
    const errors: Record<string, string> = {};
    for (const type of ["civil", "religious", "party", "baptism"] as const) {
      if (p.ceremonies[type].datetime === INCOMPLETE_DATETIME) {
        errors[`ceremonies.${type}.datetime`] = t("autosave.ceremonyDateTimeIncomplete");
      }
    }
    return errors;
  }, [t]);

  const eventAutosave = useAutosave<GeneralEventPayload, Event>({
    current: eventCurrent,
    initial: seededUuid === event.uuid ? eventInitial : null,
    resetKey: `${event.uuid}:${seededUuid === event.uuid ? "seeded" : "loading"}`,
    pendingKey: event.uuid,
    formId: EVENT_FORM_ID,
    allowedKeys: EVENT_KEYS,
    blurOnlyKeys: EVENT_BLUR_ONLY,
    groups: [EVENT_TYPE_GROUP],
    validate: validateEvent,
    save: (patch) => updateEvent(event.uuid, patch as unknown as Parameters<typeof updateEvent>[1]),
    onSaved: (updated) => onEventPatched?.(updated),
  });

  const landingAutosave = useAutosave<GeneralLandingPayload, unknown>({
    current: landingCurrent,
    initial: landingInitial,
    resetKey: `${event.uuid}:${landingLoadTick}`,
    pendingKey: event.uuid,
    formId: LANDING_FORM_ID,
    allowedKeys: LANDING_KEYS,
    validate: validateLanding,
    save: (patch) => updateEventLandingSettings(event.uuid, patch as Parameters<typeof updateEventLandingSettings>[1]),
  });

  const flushAll = useCallback(async () => {
    await Promise.all([eventAutosave.flush(), landingAutosave.flush()]);
  }, [eventAutosave, landingAutosave]);

  const retryAll = useCallback(async () => {
    await Promise.all([eventAutosave.retry(), landingAutosave.retry()]);
  }, [eventAutosave, landingAutosave]);

  /** Drop recovered drafts and show the server values again. */
  const discardDrafts = useCallback(() => {
    eventAutosave.discardDraft();
    landingAutosave.discardDraft();
    setSeededUuid(null);
    setLandingLoadTick((n) => n + 1);
  }, [eventAutosave, landingAutosave]);

  /** "Save now": shows the schema errors and sends every pending change. */
  const handleSaveNow = useCallback(async () => {
    await form.trigger();
    await flushAll();
  }, [form, flushAll]);

  return {
    form,
    loading,
    /**
     * True until the form shows the server state: the event fields (a previous
     * save may still be settling) AND the ceremonies/family baseline. Controls
     * stay disabled meanwhile, so nothing typed early is overwritten by the
     * load (JUB-152).
     */
    seeding: seededUuid !== event.uuid || landingInitial === null,
    /** A save from an earlier visit is still settling (vs. the initial load). */
    waitingForSave: saveInFlight,
    landingLoadFailed,
    /** Load the ceremonies/family baseline again after a failure. */
    retryLanding: () => setLandingLoadTick((n) => n + 1),
    ceremonies,
    family,
    setCeremonies,
    setFamily,
    handleSaveNow,
    flushAll,
    retryAll,
    flushEventOnNextChange: eventAutosave.flushOnNextChange,
    flushLandingOnNextChange: landingAutosave.flushOnNextChange,
    autosaveStatus: combineAutosaveStatus(eventAutosave.status, landingAutosave.status),
    restoredDraft: eventAutosave.restored || landingAutosave.restored,
    discardDrafts,
    fieldErrors: { ...eventAutosave.fieldErrors, ...landingAutosave.fieldErrors },
    onEventUpdated,
  };
}
